import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Tab, TabBar, TabContent, TabContents } from "./styles";

// gets the active tab key for initial component state
function getInitialActiveKey({ activeKey, defaultActiveKey, children }) {
  let key;

  if (activeKey) {
    key = activeKey;
  } else if (defaultActiveKey) {
    key = defaultActiveKey;
  } else {
    React.Children.forEach(children, child => {
      if (child && !key) key = child.key;
    });
  }

  return key;
}

// calculates where the tab content should position itself based on it
// being active or it's relationship to what is active
// notes:
//   standard offset is used to stack all the tabs on top of one another
//   relative offset is used to move the tab left and right its full width
//   based on wether it's active or its relationship to what is active
//   opacity is used to keep tabs you don't want to see translating into view
function getTabContentPosition({ activeKeyIndex, index, currentIndex, width }) {
  const position = currentIndex - activeKeyIndex;

  // standard value to stack everyone on top of one another
  const standardOffset = index * -100;

  // used to move tab left and right based on what tab is active
  let relativeOffset = 0;
  if (position <= -1) {
    relativeOffset = -100;
  } else if (position >= 1) {
    relativeOffset = 100;
  }

  return {
    transform: `translateX(${standardOffset + relativeOffset}%)`
  };
}

export const Tabs = ({
  activeKey: controlledActiveKey,
  appendTabContent = null,
  children,
  defaultActiveKey,
  onTabClick,
  prependTabContent = null,
  resizeHeight = false,
  ...props
}) => {
  const [activeKey, setActiveKey] = useState(
    getInitialActiveKey({
      activeKey: controlledActiveKey,
      defaultActiveKey,
      children
    })
  );
  useEffect(() => controlledActiveKey && setActiveKey(controlledActiveKey), [
    controlledActiveKey
  ]);

  const [width, setWidth] = useState(0);
  const [tabHeights, setTabHeights] = useState([]);
  const measuredRef = useCallback(
    node => {
      if (node !== null && resizeHeight !== undefined) {
        window.requestAnimationFrame(() => {
          setWidth(node.getBoundingClientRect().width);
          setTabHeights(
            [...node.children].map(
              tabContent => tabContent.getBoundingClientRect().height
            )
          );
        });
      }
    },
    [resizeHeight]
  );

  const keys = [];
  const tabs = [];
  const tabContents = [];

  React.Children.forEach(children, child => {
    const { key } = child;
    keys.push(key);
    tabs.push(
      <Tab
        active={key === activeKey}
        key={key}
        onClick={() => {
          if (onTabClick) onTabClick(key);
          if (!controlledActiveKey) setActiveKey(key);
        }}
      >
        {child.props.tab}
      </Tab>
    );
    tabContents.push({ key, content: child.props.children });
  });

  const activeKeyIndex = keys.indexOf(activeKey);

  return (
    <div {...props}>
      <TabBar
        tabCount={tabs.length}
        activeTabIndex={activeKeyIndex}
        className="tab-bar"
      >
        {tabs}
      </TabBar>
      {prependTabContent}
      <TabContents
        ref={measuredRef}
        className="tab-contents"
        style={{
          // auto is used to get initial height values of the tab contents
          height: tabHeights.length ? tabHeights[activeKeyIndex] : "auto"
        }}
      >
        {tabContents.map(({ key, content }, index) => (
          <TabContent
            key={key}
            style={getTabContentPosition({
              index,
              activeKeyIndex,
              currentIndex: index,
              width
            })}
          >
            {content}
          </TabContent>
        ))}
      </TabContents>
      {appendTabContent}
    </div>
  );
};

Tabs.propTypes = {
  children: PropTypes.node,
  activeKey: PropTypes.string,
  defaultActiveKey: PropTypes.string,
  onTabClick: PropTypes.func
};

// Note: TabPane is consumed above by React.Children to extract
// its props this is why no render method is implemented
export const TabPane = () => null;

TabPane.propTypes = {
  tab: PropTypes.node,
  children: PropTypes.node
};
